import React from 'react';
import { DocumentStorage } from '../../types/documentStorage';
import { useAuth } from '../../contexts/AuthContext';
import { 
  FireIcon, 
  CloudIcon, 
  ArchiveBoxIcon,
  ClockIcon,
  InformationCircleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { Squares2X2Icon as SnowflakeIcon } from '@heroicons/react/24/outline';

interface DocumentStorageInfoProps {
  document: DocumentStorage;
  compact?: boolean;
  className?: string;
}

const getStatusConfig = (status: string) => {
  switch (status) {
    case 'hot':
      return {
        icon: FireIcon,
        color: 'text-red-600 bg-red-50 border-red-200',
        label: 'Aktív',
        description: 'Gyakran használt fájl'
      };
    case 'warm':
      return {
        icon: CloudIcon,
        color: 'text-orange-600 bg-orange-50 border-orange-200',
        label: 'Rendszeres',
        description: 'Rendszeresen használt fájl'
      };
    case 'cold':
      return {
        icon: SnowflakeIcon,
        color: 'text-blue-600 bg-blue-50 border-blue-200',
        label: 'Ritkán használt',
        description: 'Ritkán elért fájl'
      };
    case 'archive':
      return {
        icon: ArchiveBoxIcon,
        color: 'text-gray-600 bg-gray-50 border-gray-200',
        label: 'Archívum',
        description: 'Archivált fájl'
      };
    case 'scheduled_deletion':
      return {
        icon: ClockIcon,
        color: 'text-red-800 bg-red-100 border-red-300',
        label: 'Törlésre ütemezett',
        description: 'Hamarosan törlésre kerül'
      };
    default:
      return {
        icon: InformationCircleIcon,
        color: 'text-gray-600 bg-gray-50 border-gray-200',
        label: 'Ismeretlen',
        description: 'Státusz elemzés alatt'
      };
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const DocumentStorageInfo: React.FC<DocumentStorageInfoProps> = ({
  document,
  compact = false,
  className = ''
}) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin';

  if (!document.lifecycle) {
    return (
      <div className={`inline-flex items-center px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600 ${className}`}>
        <ClockIcon className="w-3 h-3 mr-1" />
        Elemzés...
      </div>
    );
  }

  const { lifecycle } = document;
  const config = getStatusConfig(lifecycle.status);
  const Icon = config.icon;

  if (compact) {
    return (
      <div className={`inline-flex items-center px-2 py-1 text-xs rounded-full border ${config.color} ${className}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg border shadow-sm p-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          <div className={`inline-flex items-center px-2 py-1 text-xs rounded-full border ${config.color}`}>
            <Icon className="w-3 h-3 mr-1" />
            {config.label}
          </div>
        </div>
        <div className="text-xs text-gray-500">
          {document.fileSize && formatFileSize(document.fileSize)}
        </div>
      </div>

      {/* Description */}
      <div className="mb-3">
        <p className="text-sm text-gray-600">{config.description}</p>
      </div>

      {/* Storage Class */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Tárolási osztály:</span>
          <span className="font-medium">{lifecycle.storageClass}</span>
        </div>
      </div>

      {/* Access Info */}
      <div className="mb-3 space-y-1">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Hozzáférések:</span>
          <span className="font-medium">{lifecycle.accessCount}x</span>
        </div>
        {lifecycle.lastAccessed && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Utoljára elért:</span>
            <span className="text-xs text-gray-500">
              {new Intl.RelativeTimeFormat('hu').format(
                Math.ceil((lifecycle.lastAccessed.getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
                'day'
              )}
            </span>
          </div>
        )}
      </div>

      {/* Next Transition (without costs for non-admins) */}
      {lifecycle.nextTransition && (
        <div className="mb-3 bg-blue-50 rounded-lg p-3">
          <div className="text-sm font-medium text-blue-800 mb-1">
            Következő optimalizálás:
          </div>
          <div className="text-xs text-blue-600 space-y-1">
            <div>→ {lifecycle.nextTransition.toClass}</div>
            <div>
              {new Intl.DateTimeFormat('hu-HU', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              }).format(lifecycle.nextTransition.scheduledDate)}
            </div>
          </div>
        </div>
      )}

      {/* User-friendly Recommendations (filtered for non-admins) */}
      {lifecycle.recommendations.length > 0 && (
        <div className="space-y-1">
          <div className="text-sm font-medium text-gray-700 mb-2">
            Információk:
          </div>
          {lifecycle.recommendations
            .filter(rec => !isAdmin ? !rec.includes('$') && !rec.includes('költség') : true)
            .map((rec, index) => (
            <div key={index} className="flex items-start text-xs text-gray-600">
              <CheckCircleIcon className="w-3 h-3 mr-1 mt-0.5 text-green-500 flex-shrink-0" />
              {isAdmin ? rec : rec.replace(/\$[\d.]+/g, '').replace(/költség/g, 'optimalizálás')}
            </div>
          ))}
        </div>
      )}

      {/* Storage Optimization Status (without costs) */}
      {document.storageOptimization?.isOptimized && (
        <div className="mt-3 pt-3 border-t">
          <div className="flex items-center text-sm text-green-600">
            <CheckCircleIcon className="w-4 h-4 mr-1" />
            Optimalizált tárolás
          </div>
          {document.storageOptimization.lastOptimizedAt && (
            <div className="text-xs text-gray-500 mt-1">
              Utolsó optimalizálás: {new Intl.DateTimeFormat('hu-HU').format(document.storageOptimization.lastOptimizedAt)}
            </div>
          )}
        </div>
      )}

      {/* Optimization History (without costs for non-admins) */}
      {!isAdmin && document.storageOptimization?.optimizationHistory && document.storageOptimization.optimizationHistory.length > 0 && (
        <div className="mt-3 pt-3 border-t">
          <div className="text-sm font-medium text-gray-700 mb-2">
            Optimalizálási előzmények:
          </div>
          <div className="space-y-1">
            {document.storageOptimization.optimizationHistory.slice(-3).map((history, index) => (
              <div key={index} className="text-xs text-gray-500">
                {history.fromClass} → {history.toClass}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentStorageInfo;
